import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Divider,
  TextField,
  MenuItem
} from "@mui/material";
import { a11yProps, TabPanel } from "../../../ui";
import { Colors } from "../../../../styles/theme";
import GeneralTab, { getDefaultGeneralValues } from "./GeneralTab";
import ObjectiveTab, { getDefaultObjectiveValues } from "./ObjectiveTab";
import ExternTab, { getDefaultExternValues } from "./ExternTab";
import InternTab, { getDefaultInternValues } from "./InternTab";
import RefractTab, { getDefaultRefractValues } from "./RefractTab";
import DiagnosisTab, { getDefaultDiagnosisValues } from "./DiagnosisTab";
import { EyeExamService } from "../../../../service/eyeExamService";
import AssistPanel from "./assist/AssistPanel";
import TemplateDialog from "./TemplateDialog";
import { toast } from "react-toastify";
import { useCan } from "hooks/useCan";
import { toTitleCase } from "utils/index";
import { useLocation } from "hooks/useLocation";

export default function EyeExamEditDialog({ open, onClose, listener, patient, examData }) {
  const can = useCan();
  const [tab, setTab] = useState(0);
  const [general, setGeneral] = useState(getDefaultGeneralValues());
  const [objective, setObjective] = useState(getDefaultObjectiveValues());
  const [refract, setRefract] = useState(getDefaultRefractValues());
  const [extern, setExtern] = useState(getDefaultExternValues());
  const [intern, setIntern] = useState(getDefaultInternValues());
  const [diagnosis, setDiagnosis] = useState(getDefaultDiagnosisValues());
  const [status, setStatus] = useState('draft');
  const [saving, setSaving] = useState(false);
  const [assistOpen, setAssistOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const{ selectedLocation } = useLocation();
  const canEditEyeExam = can.action('eyeexam', 'edit');
  const header = useMemo(() => ({
    name: patient?.fullName || "",
    dob: patient?.birthdate || "",
    age: typeof patient?.age === 'number' ? `${patient.age} yrs` : "",
    healthcard: patient?.healthcard || patient?.healthCard || "",
    phone: patient?.mobile || patient?.phone || "",
  }), [patient]);

  // Hydrate from existing payload when dialog opens or exam id changes
  useEffect(() => {
    if (open) {
      if (examData?.payload?.general) {
        setGeneral({ ...getDefaultGeneralValues(), ...examData.payload.general });
      } else {
        setGeneral(getDefaultGeneralValues());
      }
      if (examData?.payload?.objective) {
        setObjective({ ...getDefaultObjectiveValues(), ...examData.payload.objective });
      } else {
        setObjective(getDefaultObjectiveValues());
      }
      if (examData?.payload?.refract) {
        setRefract({ ...getDefaultRefractValues(), ...examData.payload.refract });
      } else {
        setRefract(getDefaultRefractValues());
      }
      if (examData?.payload?.extern) {
        setExtern({ ...getDefaultExternValues(), ...examData.payload.extern });
      } else {
        setExtern(getDefaultExternValues());
      }
      if (examData?.payload?.intern) {
        setIntern({ ...getDefaultInternValues(), ...examData.payload.intern });
      } else {
        setIntern(getDefaultInternValues());
      }
      if (examData?.payload?.diagnosis) {
        setDiagnosis({ ...getDefaultDiagnosisValues(), ...examData.payload.diagnosis });
      } else {
        setDiagnosis(getDefaultDiagnosisValues());
      }
      setStatus(examData?.exam?.status || 'draft');
      setTab(0);
    }
  }, [open, examData?.exam?.id, examData?.exam?.status, examData?.payload?.general, examData?.payload?.objective, examData?.payload?.refract, examData?.payload?.extern, examData?.payload?.intern, examData?.payload?.diagnosis]);

  const handleSave = async () => {
    if (!examData?.exam?.id) return;
    const payload = { general, objective, refract, extern, intern, diagnosis };
    const chiefComplaint = general?.complaint || null;
    try {
      setSaving(true);
      const updated = await EyeExamService.update(examData.exam.id, {
        chiefComplaint,
        locationId: examData.exam.locationId || selectedLocation?.id || null,
        ...(status !== 'void' && { status }),
        payload
      });
      toast.success('Eye exam updated successfully');
      listener?.({ action: 'save', data: { exam: updated } });
      onClose?.();
    } finally { setSaving(false); }
  };

  const handleCancel = () => { listener?.({ action: 'cancel' }); onClose?.(); };

  const applyTemplate = (tplPayload) => {
    if (!tplPayload) return;
    setGeneral(prev => ({ ...prev, ...(tplPayload.general || {}) }));
    setObjective(prev => ({ ...prev, ...(tplPayload.objective || {}) }));
    setRefract(prev => ({ ...prev, ...(tplPayload.refract || {}) }));
    setExtern(prev => ({ ...prev, ...(tplPayload.extern || {}) }));
    setIntern(prev => ({ ...prev, ...(tplPayload.intern || {}) }));
    setDiagnosis(prev => ({ ...prev, ...(tplPayload.diagnosis || {}) }));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <span>Edit Eye Exam</span>
          {/* <IconButton size="small" onClick={() => setAssistOpen(true)} title="Open Assist">
            <AssistantIcon />
          </IconButton> */}
          <Button size="small" variant="outlined" onClick={() => setTemplatesOpen(true)} sx={{ ml: 1 }}>Templates</Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexWrap="wrap" alignItems="center" gap={2} mb={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{toTitleCase(header.name)}</Typography>
          {header.dob && (
            <Typography variant="body2" color={Colors.dim_grey}>DOB: {header.dob} {header.age && `(${header.age})`}</Typography>
          )}
          {header.healthcard && (
            <Typography variant="body2" color={Colors.dim_grey}>Health Card: {header.healthcard}</Typography>
          )}
          {header.phone && (
            <Typography variant="body2" color={Colors.dim_grey}>Phone: {header.phone}</Typography>
          )}
          {canEditEyeExam ? (
            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              disabled={saving || status === 'void'}
              sx={{ minWidth: 130, ml: 'auto' }}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="final">Final</MenuItem>
              {status === 'void' && <MenuItem value="void" disabled>Void</MenuItem>}
            </TextField>
          ) : (
            <Typography variant="body2" color={Colors.dim_grey} sx={{ ml: 'auto' }}>
              Status: {toTitleCase(status)}
            </Typography>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />

        <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="Eye Exam Tabs" variant="scrollable" allowScrollButtonsMobile>
          <Tab label="General" {...a11yProps('eyeexam-edit', 0)} />
          <Tab label="Obj" {...a11yProps('eyeexam-edit', 1)} />
          <Tab label="Refract." {...a11yProps('eyeexam-edit', 2)} />
          <Tab label="Extern." {...a11yProps('eyeexam-edit', 3)} />
          <Tab label="Intern." {...a11yProps('eyeexam-edit', 4)} />
          <Tab label="Diag." {...a11yProps('eyeexam-edit', 5)} />
        </Tabs>

        <TabPanel idfor="eyeexam-edit" value={tab} index={0}>
          <GeneralTab key={examData?.exam?.id || 'edit-general'} value={general} onChange={setGeneral} />
        </TabPanel>
        <TabPanel idfor="eyeexam-edit" value={tab} index={1}>
          <ObjectiveTab value={objective} onChange={setObjective} />
        </TabPanel>
        <TabPanel idfor="eyeexam-edit" value={tab} index={2}>
          <RefractTab
            value={refract}
            onChange={setRefract}
            onCopyToDiagnosisRx={(patch) => {
              setDiagnosis((prev) => {
                const next = { ...prev, rx: { ...prev.rx } };
                Object.keys(patch || {}).forEach((col) => {
                  next.rx[col] = { ...(next.rx?.[col] || {}), ...patch[col] };
                });
                if (!next.date) {
                  next.date = new Date().toISOString().split('T')[0];
                }
                return next;
              });
            }}
          />
        </TabPanel>
        <TabPanel idfor="eyeexam-edit" value={tab} index={3}>
          <ExternTab value={extern} onChange={setExtern} />
        </TabPanel>
        <TabPanel idfor="eyeexam-edit" value={tab} index={4}>
          <InternTab value={intern} onChange={setIntern} />
        </TabPanel>
        <TabPanel idfor="eyeexam-edit" value={tab} index={5}>
          <DiagnosisTab value={diagnosis} onChange={setDiagnosis} />
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={handleCancel} disabled={saving}>Cancel</Button>
        {canEditEyeExam && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        )}
      </DialogActions>
      <AssistPanel open={assistOpen} onClose={() => setAssistOpen(false)} general={general} objective={objective} />
      <TemplateDialog
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        practitionerId={examData?.exam?.practitionerId}
        currentPayload={{ general, objective, refract, extern, intern, diagnosis }}
        onApplyTemplate={applyTemplate}
      />
    </Dialog>
  );
}
